import React from "react";

import { Box, MenuItem, Select, styled } from "@mui/material";
import { Bar, Doughnut, Line, Pie } from "react-chartjs-2";

import { CHART_TYPE_OPTIONS, ChartType } from "./sequence/MeReport";

// Chart canvas should be tall enough to make labels readable but not dominate the chat scroll
const CHART_HEIGHT = 280;

const Container = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  padding: "12px",
  borderRadius: "0.5rem",
  border: "1px solid #e2e8f0",
  backgroundColor: "#fafbfc",
});

const TypeSelect = styled(Select)({
  fontSize: "0.75rem",
  minWidth: "8rem",
  backgroundColor: "#ffffff",
  borderRadius: "0.375rem",
  alignSelf: "flex-start",
  "& .MuiOutlinedInput-notchedOutline": { border: "1px solid #e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#e91e63" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#e91e63" },
  "& .MuiSelect-select": { padding: "6px 10px" },
});

const CanvasHolder = styled(Box)({
  position: "relative",
  width: "100%",
  height: `${CHART_HEIGHT}px`,
  backgroundColor: "#ffffff",
  borderRadius: "0.375rem",
  padding: "8px",
  boxSizing: "border-box",
});

const EmptyState = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  padding: "0 2rem",
  "& > div": {
    maxWidth: "32rem",
    fontSize: "0.85rem",
    lineHeight: 1.6,
    color: "#94a3b8",
    fontStyle: "italic",
    textAlign: "center",
  },
  "& code": {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
    fontStyle: "normal",
    fontSize: "0.78rem",
    backgroundColor: "#f1f5f9",
    color: "#475569",
    padding: "1px 6px",
    borderRadius: "4px",
  },
  "& b": {
    color: "#64748b",
  },
});

type ChartPanelProps = {
  chartData: any;
  chartType: ChartType;
  onChangeChartType: (chartType: ChartType) => void;
};

const renderChart = (chartData: any, chartType: ChartType) => {
  const labelCount = chartData?.labels?.length || 0;
  const datasets: any[] = chartData?.datasets || [];

  // Non-numeric guard: backend returned string values (e.g. last_name, gender)
  // that got coerced to 0. Warn instead of rendering a misleading empty chart.
  if (chartData?._nonNumeric) {
    const badCols = datasets.map((ds) => ds.label).filter(Boolean).join(", ") || "value";
    return (
      <EmptyState>
        <div>
          Chart can't render — value column(s) <b>"{badCols}"</b> contain non-numeric data.
          Use an aggregation query, e.g.{" "}
          <code>SELECT col, COUNT(*) ... GROUP BY col</code>
        </div>
      </EmptyState>
    );
  }

  // Empty-data guard: only block when there's nothing to plot at all.
  // All-zero data is valid (= "0 records in each category") and should render
  // as empty bars so the user still sees the labels.
  const hasData =
    labelCount > 0 &&
    datasets.length > 0 &&
    datasets.some((ds) => (ds.data || []).length > 0);

  if (!hasData) {
    return (
      <EmptyState>
        <div>
          Chart has no data — query returned no rows. Try a different query or check filters.
        </div>
      </EmptyState>
    );
  }

  // Auto-hide legend when too many entries (>15) — legend grid explodes otherwise
  const showLegend = labelCount <= 15;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: showLegend,
      position: "bottom" as const,
      labels: { fontFamily: "Inter, sans-serif", fontSize: 11, boxWidth: 12 },
    },
  };

  // Explicit height ensures Chart.js v2 canvas fills CanvasHolder (otherwise leaves empty space)
  const props = { data: chartData, options, height: CHART_HEIGHT };
  switch (chartType) {
    case "line":
      return <Line {...props} />;
    case "pie":
      return <Pie {...props} />;
    case "doughnut":
      return <Doughnut {...props} />;
    case "bar":
    default:
      return <Bar {...props} />;
  }
};

const ChartPanel: React.FC<ChartPanelProps> = ({ chartData, chartType, onChangeChartType }) => (
  <Container>
    <TypeSelect
      value={chartType}
      size="small"
      onChange={(e) => onChangeChartType(e.target.value as ChartType)}
    >
      {CHART_TYPE_OPTIONS.map((opt) => (
        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "0.8rem" }}>
          {opt.label}
        </MenuItem>
      ))}
    </TypeSelect>
    <CanvasHolder>{renderChart(chartData, chartType)}</CanvasHolder>
  </Container>
);

export default ChartPanel;
